import { Button, Stack, styled } from "@mui/material";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import {
  type GridSelectionModel,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { format } from "date-fns";
import {
  type ToolbarProps,
  MonitoringDetailToolbar,
} from "./MonitoringDetailToolbar";
import { useGridPagination } from "../hooks/useGridPagination";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { type MonitoringDataGridRow } from "../types";
import { DataGrid } from "./DataGrid";
import { type NavigateFunction } from "react-router-dom";

interface IProps {
  handleRowSelectionChange: (newSelectionModel: GridSelectionModel) => void;
  navigate: NavigateFunction;
  rows: MonitoringDataGridRow[];
  setSelectedIds: React.Dispatch<React.SetStateAction<GridSelectionModel>>;

  setAcquireBookOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const StyledDataGrid = styled(DataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

const makeGridColDef = (
  navigate: NavigateFunction,
  setSelectedIds: React.Dispatch<React.SetStateAction<GridSelectionModel>>,
  setAcquireBookOpen: React.Dispatch<React.SetStateAction<boolean>>,
  isReadOnly: boolean
): GridColDef[] => {
  return [
    {
      field: "realEstateType",
      headerName: "不動産種別",
      width: 100,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "prefectureName",
      headerName: "都道府県",
      width: 100,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "location",
      headerName: "所在",
      width: 300,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "chibanKaokuNumber",
      headerName: "地番または家屋番号",
      width: 220,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "registrationDate",
      headerName: "登録日",
      width: 120,
      sortable: false,
      align: "left",
      headerAlign: "left",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        if (value != null) return format(new Date(value), "yyyy/MM/dd");
      },
    },
    {
      field: "remarks",
      headerName: "備考",
      width: 440,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 300,
      sortable: false,
      align: "left",
      headerAlign: "left",
      disableExport: true,
      renderCell: ({
        row,
      }: GridRenderCellParams<string, MonitoringDataGridRow>) => {
        return (
          <Stack direction={"row"} spacing={1}>
            <Button
              onClick={() => {
                navigate(`/monitoring/detail/${row.id}`);
              }}
              sx={{
                bgcolor: "#F4A150",
                "&:hover": {
                  backgroundColor: "#DF9B4A",
                },
              }}
              variant="contained"
            >
              モニタリング結果
            </Button>
            <ReadOnlyTooltip isReadOnly={isReadOnly}>
              <Button
                onClick={() => {
                  setSelectedIds([row.id]);
                  setAcquireBookOpen(true);
                }}
                variant="outlined"
                disabled={isReadOnly}
              >
                登記取得
              </Button>
            </ReadOnlyTooltip>
          </Stack>
        );
      },
    },
  ];
};

/**
 * 不動産登記モニタリングの表部分
 * @param {IProps} props
 * @returns
 */
export const MonitoringDetailDataGrid: React.FC<IProps> = (props: IProps) => {
  const {
    handleRowSelectionChange,
    navigate,
    rows,
    setSelectedIds,
    setAcquireBookOpen,
  } = props;
  const isReadOnly = useReadOnlyUser();
  const colDef = makeGridColDef(
    navigate,
    setSelectedIds,
    setAcquireBookOpen,
    isReadOnly
  );
  const { page, pageSize, changePage, changePageSize } = useGridPagination();
  const rowsPerPageOptions: number[] = [25, 50, 100];

  const dataGridPaginationProps = {
    page,
    pageSize,
    rowsPerPageOptions,
    onPageChange: changePage,
    onPageSizeChange: changePageSize,
  };

  const toolbarProps: ToolbarProps = {
    page,
    pageSize,
    rowsPerPageOptions,
    changePage,
    changePageSize,
    allCount: rows.length,
  };

  return (
    <StyledDataGrid
      stickyHeader={true}
      autoHeight
      columns={colDef}
      disableColumnMenu
      disableSelectionOnClick={true}
      onSelectionModelChange={handleRowSelectionChange}
      rows={rows}
      sortModel={[
        {
          field: "latestReceptionDate",
          sort: "desc",
        },
      ]}
      {...dataGridPaginationProps}
      components={{
        Toolbar: MonitoringDetailToolbar,
      }}
      componentsProps={{
        toolbar: {
          ...toolbarProps,
        },
      }}
    />
  );
};
